import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaCode,
  FaServer,
  FaWindows,
  FaBug,
  FaHistory,
  FaLaptopCode,
  FaExclamationTriangle,
  FaTasks,
  FaGlobe,
  FaFileWord,
  FaCogs,
  FaSkullCrossbones,
  FaExchangeAlt,
  FaBook,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const Vbscript = () => {
  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
  }, []);

  const roadmap = [
    {
      step: "Step 1",
      title: "Introduction",
      desc: "History of VBScript and its role in Microsoft environments.",
      icon: <FaHistory />,
    },
    {
      step: "Step 2",
      title: "Syntax Basics",
      desc: "Variables, data types, operators, and control flow.",
      icon: <FaCode />,
    },
    {
      step: "Step 3",
      title: "Procedures & Functions",
      desc: "Reusable blocks of VBScript code.",
      icon: <FaLaptopCode />,
    },
    {
      step: "Step 4",
      title: "File System Objects",
      desc: "Working with files, folders, and drives.",
      icon: <FaServer />,
    },
    {
      step: "Step 5",
      title: "Error Handling",
      desc: "On Error Resume Next, debugging, and pitfalls.",
      icon: <FaBug />,
    },
    {
      step: "Step 6",
      title: "Automation",
      desc: "How VBScript automated tasks in Windows & IE.",
      icon: <FaWindows />,
    },
    {
      step: "Step 7",
      title: "Limitations",
      desc: "Security issues and why VBScript is deprecated.",
      icon: <FaExclamationTriangle />,
    },
  ];

  const usageAreas = [
    {
      title: "Windows Automation",
      desc: "VBScript automated system tasks and administration.",
      icon: <FaTasks />,
    },
    {
      title: "Internet Explorer",
      desc: "Used for client-side scripting in IE (no longer supported).",
      icon: <FaGlobe />,
    },
    {
      title: "Office Macros",
      desc: "Automated tasks in Microsoft Word and Excel.",
      icon: <FaFileWord />,
    },
    {
      title: "Legacy Applications",
      desc: "Still seen in old enterprise apps.",
      icon: <FaCogs />,
    },
  ];

  const futureScope = [
    {
      title: "Deprecation",
      text: "Microsoft officially ended support for VBScript in 2019.",
      icon: <FaSkullCrossbones />,
    },
    {
      title: "Security Risks",
      text: "VBScript is blocked in modern browsers due to malware risks.",
      icon: <FaExclamationTriangle />,
    },
    {
      title: "Alternatives",
      text: "PowerShell and JavaScript replaced VBScript in automation.",
      icon: <FaExchangeAlt />,
    },
    {
      title: "Learning Value",
      text: "Good for scripting history, but not practical today.",
      icon: <FaBook />,
    },
  ];

  return (
    <div className="bg-gradient-to-br from-gray-900 via-black to-gray-800 text-gray-100">
      {/* HERO */}
      <header
        className="relative py-28 text-center overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage:
            "url('/assets/d138bc80646fbb90c061fd7ea38a43fe.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative z-10">
          <h1
            data-aos="zoom-in"
            className="text-4xl md:text-6xl font-extrabold text-yellow-400 drop-shadow-lg neon-text mt-5"
          >
            VBScript – The Legacy Scripting Language
          </h1>
          <p
            data-aos="fade-up"
            className="mt-4 text-lg md:text-xl text-gray-300 max-w-2xl mx-auto"
          >
            Explore the scripting language that shaped Windows automation ⚡
          </p>
          <Link to="/contact">
            <button
              className="mt-8 px-8 py-3 rounded-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold shadow-xl transition animate-bounce"
              data-aos="zoom-in"
            >
              Enroll Now 🚀
            </button>
          </Link>
        </div>
      </header>

      {/* ROADMAP */}
      <section className="py-16 px-6 bg-white">
        <h2
          className="text-3xl md:text-4xl font-bold text-center mb-12 text-black"
          data-aos="fade-up"
        >
          VBScript Learning Roadmap
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {roadmap.map((item, i) => (
            <div
              key={i}
              className={`p-6 rounded-xl bg-white border border-gray-200 shadow-md hover:shadow-yellow-400/50 transition flex flex-col items-center text-center ${
                i % 2 === 0 ? "animate-float" : "animate-swing"
              }`}
              data-aos="zoom-in"
              data-aos-delay={i * 150}
            >
              <div className="w-16 h-16 flex items-center justify-center text-2xl text-black bg-yellow-400 rounded-full shadow-md mb-4 animate-pulse">
                {item.icon}
              </div>
              <h3 className="text-lg font-bold text-black mb-2">
                {item.step}: {item.title}
              </h3>
              <p className="text-gray-700 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* USAGE */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-800 to-black">
        <h2
          className="text-3xl md:text-4xl font-bold text-center mb-12 text-yellow-300"
          data-aos="fade-up"
        >
          Where VBScript Was Used
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto justify-items-center">
          {usageAreas.map((area, i) => (
            <div
              key={i}
              className="bg-gray-900 rounded-2xl p-6 text-center shadow-md hover:shadow-yellow-500/30 transition animate-glow flex flex-col items-center"
              data-aos="zoom-in"
              data-aos-delay={i * 150}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-yellow-400 text-black text-xl mb-3 animate-bounce">
                {area.icon}
              </div>
              <h3 className="text-lg font-bold text-yellow-400">
                {area.title}
              </h3>
              <p className="text-gray-400 mt-2 text-sm">{area.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FUTURE */}
      <section className="py-16 px-6">
        <h2
          className="text-3xl md:text-4xl font-bold text-center mb-12 text-yellow-300"
          data-aos="fade-up"
        >
          The Future of VBScript
        </h2>
        <div className="flex flex-wrap justify-center gap-6 max-w-6xl mx-auto">
          {futureScope.map((item, i) => (
            <div
              key={i}
              className="w-64 bg-gradient-to-br from-gray-900 to-gray-800 p-6 rounded-2xl border border-yellow-500/30 shadow-md hover:shadow-yellow-500/40 transition flex flex-col items-center text-center animate-float"
              data-aos="fade-up"
              data-aos-delay={i * 150}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-yellow-400 text-black text-xl mb-3 animate-spin-slow">
                {item.icon}
              </div>
              <h3 className="text-xl font-bold text-yellow-400">
                {item.title}
              </h3>
              <p className="text-gray-300 mt-2">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <style>{`
  @keyframes spin-slow {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  .animate-spin-slow {
    animation: spin-slow 8s linear infinite;
  }
`}</style>

      {/* FOOTER */}
      <footer className="py-10 text-center bg-black/90">
        <p className="text-gray-400 text-sm">
          ⚡ VBScript is deprecated – but learning it helps understand the roots
          of automation scripting.
        </p>
        <Link to="/contact">
          <button className="mt-6 px-8 py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-full shadow-lg animate-bounce">
            Enroll Now
          </button>
        </Link>
      </footer>

      {/* Animations */}
      <style>{`
        .neon-text {
          text-shadow: 0 0 5px #facc15, 0 0 20px #facc15, 0 0 40px #facc15;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-float { animation: float 3s infinite ease-in-out; }

        @keyframes swing {
          20% { transform: rotate(8deg); }
          40% { transform: rotate(-6deg); }
          60% { transform: rotate(4deg); }
          80% { transform: rotate(-2deg); }
          100% { transform: rotate(0deg); }
        }
        .animate-swing { animation: swing 2.5s infinite ease-in-out; }

        @keyframes glow {
          0%, 100% { box-shadow: 0 0 10px #facc15, 0 0 20px #facc15; }
          50% { box-shadow: 0 0 20px #fde047, 0 0 40px #fde047; }
        }
        .animate-glow { animation: glow 3s infinite alternate; }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow { animation: spin-slow 10s linear infinite; }
      `}</style>
    </div>
  );
};

export default Vbscript;
